import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, computed, effect, inject, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { IMAGE_URLS } from 'src/app/core/constants/image-source';
import { CartRepositoryService } from 'src/app/core/services/cart/cart-repository.service';
import { NotificationService } from 'src/app/core/services/notification/notification.service';
import { MyOverlayRef } from 'src/app/core/services/overlay/myoverlay-ref';
import { StoreConfigurationRepositoryService } from 'src/app/core/services/store-configuration/store-configuration-repository.service';
import { mapImageUrl } from 'src/app/products/components/product-detail/product-detail.component';
import { OtherOffers, Product, ProductDealCountdown } from 'src/app/products/models/product.model';
import { PriceService } from 'src/app/products/services/price.service';
import { ProductAttributeSelectionsService } from 'src/app/products/services/product-attribute-selections.service';


import { HttpErrorResponse } from '@angular/common/http';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { UpdateQuantity } from 'src/app/cart/components/cart/cart.component';
import { ImageViewerComponent } from 'src/app/category/components/product-detail-overlay/image-viewer/image-viewer.component';
import { FAILED_TO_LOAD_PRODUCT_MESSAGE } from 'src/app/core/constants/error-messages';
import { ServerError } from 'src/app/core/models/server-error.model';
import { Action, GoogleAnalyticsService } from 'src/app/core/services/analytics/google-analytics.service';
import { BrowserApiService } from 'src/app/core/services/browser-api/browser-api.service';
import { CartItem, UpdateCart } from 'src/app/core/services/cart/cart-api.service';
import { JsonLdService } from 'src/app/core/services/json-ld/json-ld.service';
import { OverlayService } from 'src/app/core/services/overlay/overlay.service';
import { PlatformService } from 'src/app/core/services/platform/platform.service';
import { SeoService } from 'src/app/core/services/seo/seo.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { AboutBiddingOverlayComponent } from 'src/app/products/components/about-bidding-overlay/about-bidding-overlay.component';
import { BiddingOverlayComponent } from 'src/app/products/components/bidding-overlay/bidding-overlay.component';
import { DeliveryEtdStatusComponent } from "src/app/products/components/delivery-etd-status/delivery-etd-status.component";
import { LoginOverlayStateService } from 'src/app/products/components/login-overlay-state/login-overlay-state.service';
import { DealCountdownService, RemainingTime } from 'src/app/products/services/deal-countdown.service';
import { ProductDetailService } from 'src/app/products/services/product-detail.service';
import { ProductsRepositoryService } from 'src/app/products/services/products-repository.service';
import { IndianCurrencyPipe } from 'src/app/shared/pipes/indian-currency.pipe';
import { PercentageDiscountPipe } from 'src/app/shared/pipes/percentage-discount.pipe';
import { CartData } from '../../../core/interfaces/cart.interface';
import { ProductAvailableOffersComponent } from "../../../products/components/dynamic-product-details/product-details-template/product-primary-details/product-avilable-offers/product-available-offers.component";
import { ProductDetailOverlayShimmerComponent } from "./product-detail-overlay-shimmer/product-detail-overlay-shimmer.component";
import { ProductVariantComponent } from "./product-variant/product-variant.component";
import { RazorpayAffordabilityWidgetComponent } from "./razorpay-affordability-widget/razorpay-affordability-widget.component";

@Component({
  standalone: true,
  selector: 'webstore-product-detail-overlay',
  templateUrl: './product-detail-overlay.component.html',
  styleUrls: ['./product-detail-overlay.component.scss'],
  providers: [DealCountdownService],
  imports: [
    CommonModule,
    RouterModule,
    ProductVariantComponent,
    ImageViewerComponent,
    ProductDetailOverlayShimmerComponent,
    RazorpayAffordabilityWidgetComponent,
    DeliveryEtdStatusComponent,
    ProductAvailableOffersComponent,

    IndianCurrencyPipe,
    PercentageDiscountPipe
  ]
})
export class ProductDetailOverlayComponent implements OnInit, AfterViewInit, OnDestroy {
  private priceService = inject(PriceService);
  private myOverlayRef = inject(MyOverlayRef);
  private productAttributeSelectionsService = inject(ProductAttributeSelectionsService);
  private productsRepositoryService = inject(ProductsRepositoryService);
  private cartRepositoryService = inject(CartRepositoryService);
  private notificationService = inject(NotificationService);
  private seoService = inject(SeoService);
  private jsonLdService = inject(JsonLdService);
  private router = inject(Router);
  private userService = inject(UserService);
  private storeConfigurationRepositoryService = inject(StoreConfigurationRepositoryService);
  private productDetailService = inject(ProductDetailService);
  private loginOverlayStateService = inject(LoginOverlayStateService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private overlayService = inject(OverlayService);

  private platformService = inject(PlatformService);
  private dealContDown = inject(DealCountdownService);
  private browserApiService = inject(BrowserApiService);

  constructor() {
    // Set up overlay state effect in constructor (Angular 20 best practice)
    this.setupOverlayStateEffect();
    this.setupCartStateEffect();
    this.setupProductUrlEffect();
  }


  @ViewChild('productOverlay') productOverlay!: ElementRef;

  IMAGE_URLS = IMAGE_URLS;

  private _sellingPrice = signal<number>(0);
  readonly sellingPrice = this._sellingPrice.asReadonly();

  private _percentSavings = signal<number>(0);
  readonly percentSavings = this._percentSavings.asReadonly();

  private _actualPrice = signal<number>(0);
  readonly actualPrice = this._actualPrice.asReadonly();

  private _endDay = signal<Date>(new Date());
  readonly endDay = this._endDay.asReadonly();

  private _isDealAvailable = signal<boolean>(false);
  readonly isDealAvailable = this._isDealAvailable.asReadonly();

  private _productDealCountDown = signal<ProductDealCountdown | undefined>(undefined);
  readonly productDealCountDown = this._productDealCountDown.asReadonly();

  private _remainingTime = signal<RemainingTime | null>(null);
  readonly remainingTime = this._remainingTime.asReadonly();



  private _bidPrice = signal<number>(0);
  readonly bidPrice = this._bidPrice.asReadonly();

  private _hasVariant = signal<boolean>(false);
  readonly hasVariant = this._hasVariant.asReadonly();

  private _product = signal<Product | null>(null);
  readonly product = this._product.asReadonly();

  private _isCartItem = signal<boolean>(false);
  readonly isCartItem = this._isCartItem.asReadonly();

  private _cartItem = signal<CartItem | null>(null);
  readonly cartItem = this._cartItem.asReadonly();

  private _error = signal<string | null>(null);
  readonly error = this._error.asReadonly();

  private _availableOffers = signal<OtherOffers[]>([]);
  readonly availableOffers = this._availableOffers.asReadonly();

  readonly showEstimatedDelivery = this.storeConfigurationRepositoryService.showEstimatedDelivery();
  showProductBidding = computed(() => this.storeConfigurationRepositoryService.showProductBidding());
  readonly storeConfiguration = this.storeConfigurationRepositoryService.storeConfigurations();
  biddingRef!: MyOverlayRef<BiddingOverlayComponent, { productId: string, selling_price: number }, { bid_price: number }>

  readonly currentUser = computed(() => this.userService.appUser?.user || null);

  readonly isDealActive = computed(() =>
    this.isDealAvailable() &&
    this.platformService.isBrowser() &&
    this.productDealCountDown()
  );

  readonly hasAvailableOffers = computed(() => this.availableOffers().length > 0);

  readonly productImages = computed(() => {
    const product = this.product();
    return product ? mapImageUrl(product.featured_images || []).filter((url): url is string => url !== undefined) : [];
  });

  // Convert product setter/getter to signal-based approach
  setProduct(product: Product | null): void {
    if (product) {
      this._product.set(product);

      const { actualPrice, sellingPrice } = this.priceService.getProductPrice(product);
      this._sellingPrice.set(sellingPrice);
      this._actualPrice.set(actualPrice);
      this._isDealAvailable.set(!!product.deal);
      this._productDealCountDown.set(product.deal);
      this._availableOffers.set(product.price_list.other_offers);

      if (this.isDealActive()) {
        const dealEndTime = this.productDealCountDown()?.end_time;
        if (dealEndTime) {
          this._endDay.set(new Date(dealEndTime));
          this.dealContDown.getCountDown(this.endDay());
          // Set up deal countdown tracking
          this.startDealCountdown();
        }
      }
      this.scrollToTop();
    } else {
      this._product.set(null);
    }
  }

  getProduct(): Product | null {
    return this.product();
  }

  setProductUrlPath(urlPath: string): void {
    if (urlPath && urlPath !== this._productUrlPathSignal()) {
      this._productUrlPathSignal.set(urlPath);
    }
  }

  private _productUrlPathSignal = signal<string | null>(null);
  readonly productUrlPathSignal = this._productUrlPathSignal.asReadonly();

  currentAttributeSelections!: FormGroup;
  // currentAttributeSelections$: Observable<any>;

  private _loadingProduct = signal<boolean>(true);
  readonly loadingProduct = this._loadingProduct.asReadonly();

  private _variantLoading = signal<boolean>(false);
  readonly variantLoading = this._variantLoading.asReadonly();

  private _isLoadingProduct = signal<boolean>(false);

  private _overlayMasking = signal<boolean>(false);
  readonly overlayMasking = this._overlayMasking.asReadonly();

  private _hideOverlay = signal<boolean>(false);
  readonly hideOverlay = this._hideOverlay.asReadonly();







  readonly user = computed(() => this.userService.appUser?.user || null);






  ngOnInit(): void {
    if (this.myOverlayRef.data) {
      this._productUrlPathSignal.set(this.myOverlayRef.data);
    }

    // Product loading is now handled by setupProductUrlEffect() in constructor
    // Cart state tracking is now handled by setupCartStateEffect() in constructor

    // Set up login state tracking
    this.checkLoginState();
  }

  private async loadProduct(url: string): Promise<void> {
    this._isLoadingProduct.set(true);
    
    try {
      this.scrollToTop();
      
      const isVariantChange = this._product() !== null;
      
      if (isVariantChange) {
        this._variantLoading.set(true);
      } else {
        this.productShowLoader();
      }

      await this.productsRepositoryService.getProduct(url, true);
      const product = this.productsRepositoryService.product();

      if (product) {
        this.setProduct(product);

        this.seoService.updateProductName(product?.name);
        this.seoService.updateTitle(product?.name);
        this.seoService.updateSeoInformation(product);
        this.jsonLdService.insertSchema(JsonLdService.productSchema(product));

        const productForForm = this.getProduct()!;
        this.productAttributeSelectionsService.prepareAttributeForm(productForForm);
        this.currentAttributeSelections = this.productAttributeSelectionsService?.attributeSelections;

        const hasVariantAttributes = Object.keys(this.currentAttributeSelections?.value || {}).length > 0;
        this._hasVariant.set(hasVariantAttributes);

        setTimeout(() => {
          if (isVariantChange) {
            this._variantLoading.set(false);
          } else {
            this.productHideLoader();
          }
          this._isLoadingProduct.set(false);
        }, 150);
      } else {
        throw new Error('Product not found after loading');
      }
    } catch (error) {
      console.error('❌ [Overlay] Error loading product:', error);
      this.productHideLoader();
      this.closeProductDetail();
      this.notificationService.showError(FAILED_TO_LOAD_PRODUCT_MESSAGE);
      this._error.set(FAILED_TO_LOAD_PRODUCT_MESSAGE);
      this._isLoadingProduct.set(false);
    }
  }

  ngAfterViewInit(): void {
    // View initialization complete
  }

  ngOnDestroy(): void {
    // Reset SEO meta tags to default thumbnail
    this.seoService.updateThumbnailMetaTags();
    // Cleanup - signals don't need explicit cleanup like observables
  }

  increaseQuantity(updatedQuantity: UpdateQuantity) {
    this.updateQuantity(updatedQuantity);
  }

  decreaseQuantity(updatedQuantity: UpdateQuantity) {
    const { quantity, cartItemId } = updatedQuantity;
    if (quantity === 0) {
      this.removeCartItem(cartItemId);
    } else {
      this.updateQuantity(updatedQuantity);
    }
  }

  updateQuantity(updatedQuantity: UpdateQuantity) {

    const { cartItemId, quantity } = updatedQuantity;

    const reqObj: UpdateCart = {
      cart_items: [{
        id: cartItemId,
        quantity
      }]
    };
    this._overlayMasking.set(true);
    this.cartRepositoryService.updateCart(reqObj).then(() => {
      this._overlayMasking.set(false);
    }).catch((error: HttpErrorResponse) => {
      this._overlayMasking.set(false);
      if (error instanceof HttpErrorResponse) {
        const errorDetail: ServerError = error?.error;
        this._error.set(errorDetail?.message || 'Update failed');
        const message = errorDetail?.detail?.[0]?.error;
        if (typeof message === 'string') {
          this.notificationService.showError(`${message}`);
        }
      }
    });
  }

  removeCartItem(cartItemId: string) {
    this._overlayMasking.set(true);
    this.cartRepositoryService.removeProductFromCart(cartItemId).then(() => {
      this._overlayMasking.set(false);
    }).catch((error: any) => {
      this._overlayMasking.set(false);
    });
  }

  addToCart(cartDetail: CartData) {
    const currentProduct = this.product();

    if (currentProduct) {
      const loginRedirectHappened = this.productDetailService.addToCart({
        ...cartDetail,
        product: currentProduct,
        stayOnCurrentPage: cartDetail.stayOnCurrentPage ?? false  // Use the provided value or default to false
      });

      // Close overlay if login redirect happened
      if (loginRedirectHappened) {
        this.closeProductDetail();
      }
    }
  }

  navigateToProductDetailPage(route: string) {
    const url = this.router.serializeUrl(this.router.createUrlTree([route]));
    // Open in new tab with security features
    this.browserApiService.open(url, 'product-detail-page');
  }

  navToCart() {
    this.closeProductDetail();
    this.router.navigate(['/cart']);
  }

  productShowLoader() {
    this._overlayMasking.set(true);
    this._loadingProduct.set(true);
  }

  productHideLoader() {
    this._overlayMasking.set(false);
    this._loadingProduct.set(false);
  }

  openBiddingOverlay() {
    const user = this.userService.appUser;
    if (!user?.access_token) {
      // Close the product detail overlay before navigation
      this.closeProductDetail();

      // Navigate to auth with return URL
      const currentUrl = this.router.url;
      this.router.navigate(['/auth/login-otp'], {
        queryParams: { returnUrl: currentUrl }
      });
      this.notificationService.showInfo('Please log in to your account to continue.');
    } else {
      this.loginOverlayStateService.setLoginOverlayState(true);
      this.googleAnalyticsService.injectScript({ action: Action.BidInitiated })
      this.biddingRef = this.overlayService.open<BiddingOverlayComponent, { productId: string, selling_price: number }>(BiddingOverlayComponent, {
        productId: this.product()?.id || '',
        selling_price: this.sellingPrice()
      }, true, 'centered', '', 'block-scroll');

      // Set up bidding overlay event handlers
      this.setupBiddingOverlayHandlers();
    }
  }

  openAboutBiddingOverlay() {
    this.loginOverlayStateService.setLoginOverlayState(true);
    const aboutBiddingRef = this.overlayService.open(AboutBiddingOverlayComponent, null, true, 'centered');
    // Set up about bidding overlay event handlers
    this.setupAboutBiddingOverlayHandlers(aboutBiddingRef);
  }

  scrollToTop() {
    if (this.platformService.isBrowser() && this.productOverlay?.nativeElement) {
      this.productOverlay.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }


  closeProductDetail() {
    this.myOverlayRef.close()
  }

  private checkLoginState(): void {
    const loginCompleted = this.productDetailService.loginCompleted();
    if (loginCompleted) {
      this.openBiddingOverlay();
    }
  }

  private setupOverlayStateEffect(): void {
    // Use effect to react to login overlay state changes (Angular 20 way)
    effect(() => {
      const isLoginOverlayOpen = this.loginOverlayStateService.isLoginOverlayOpen();
      this._hideOverlay.set(isLoginOverlayOpen);
    });
  }

  private setupCartStateEffect(): void {
    // This replaces the old combineLatest([product$, cart$]) pattern
    effect(() => {
      const product = this.product();
      const cart = this.cartRepositoryService.cart();

      if (product && cart) {
        const matchedCartItem = cart.cart_items.find(
          cartItem => cartItem.product?.id === product.id && !cartItem.is_product_offer_item
        );

        if (matchedCartItem) {
          this._isCartItem.set(true);
          this._cartItem.set(matchedCartItem);
        } else {
          this._isCartItem.set(false);
          this._cartItem.set(null);
        }
      }
    });
  }

  private setupProductUrlEffect(): void {
    effect(() => {
      const url = this.productUrlPathSignal();
      const currentProduct = this._product();
      const isLoading = this._isLoadingProduct();
      
      if (!url || isLoading || currentProduct?.url_path === url) {
        return;
      }
      
      void this.loadProduct(url);
    });
  }

  private startDealCountdown(): void {
    // Use setInterval for countdown updates instead of effect
    const countdownInterval = setInterval(() => {
      const time = this.dealContDown.remainingTime();
      if (time) {
        this._remainingTime.set(time);
      } else {
        // Clear interval when countdown is complete
        clearInterval(countdownInterval);
      }
    }, 1000); // Update every second
  }

  private setupBiddingOverlayHandlers(): void {
    // Set up data reception handler using Observable (proper Angular way)
    this.biddingRef.receiveData$.pipe(
      tap((data) => {
        // Close bidding overlay first
        this.biddingRef.close();
        // Then add to cart without closing product detail overlay
        const currentProduct = this.product();
        if (currentProduct) {
          this.productDetailService.addToCart({
            product: currentProduct,
            bid_price: data.data.bid_price,
            productDetailOverlay: true,
            stayOnCurrentPage: true
          });
        }
      })
    ).subscribe();

    // Set up close handler using Observable
    this.biddingRef.afterClosed$.pipe(
      tap(() => {
        this.loginOverlayStateService.setLoginOverlayState(false);
      })
    ).subscribe();
  }

  private setupAboutBiddingOverlayHandlers(aboutBiddingRef: MyOverlayRef<AboutBiddingOverlayComponent>): void {
    // Set up close handler using Observable
    aboutBiddingRef.afterClosed$.pipe(
      tap(() => {
        this.loginOverlayStateService.setLoginOverlayState(false);
      })
    ).subscribe();
  }



}



